import React, {FC, useEffect, useMemo, useState} from 'react'
import {SearchMode} from '../model/Pegadaian.types'
import {toAbsoluteUrl} from '../../../../../_metronic/helpers'

interface Props {
  mode: SearchMode
  searchValue: string
  setSearchValue: (val: string) => void
  onClose: () => void
  onSelect: (loc: string, mode: SearchMode) => void
  fetchOrigins: (keyword: string) => Promise<string[]>
  fetchDestinations: (origin: string, keyword: string) => Promise<string[]>
  selectedOrigin: string
}

const LocationSearchOverlay: FC<Props> = ({
  mode,
  searchValue,
  setSearchValue,
  onClose,
  onSelect,
  fetchOrigins,
  fetchDestinations,
  selectedOrigin,
}) => {
  // State untuk menyimpan semua data asli dari API
  const [allLocations, setAllLocations] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  // 1. Fetch data awal saat overlay dibuka
  useEffect(() => {
    const initData = async () => {
      try {
        setLoading(true)
        let data: string[] = []
        if (mode === 'origin') {
          data = await fetchOrigins('') // Ambil semua origin
        } else {
          data = await fetchDestinations(selectedOrigin, '') // Ambil semua destinasi berdasarkan origin
        }
        setAllLocations(data)
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    initData()
  }, [mode, selectedOrigin])

  const filteredResults = useMemo(() => {
    if (!searchValue.trim()) return allLocations
    
    const term = searchValue.toLowerCase()
    return allLocations.filter((loc) => 
      loc.toLowerCase().includes(term)
    )
  }, [searchValue, allLocations])

  const title = mode === 'origin' ? 'Cari Lokasi Keberangkatan' : 'Cari Lokasi Tujuan'
  const placeholder = mode === 'origin' ? 'Keberangkatan' : 'Tujuan'
  const point = mode === 'origin'? 'Titik Keberangkatan' : 'Titik Penurunan'

  return (
    <div
      className='position-absolute top-0 start-0 w-100 h-100 bg-white'
      style={{zIndex: 1050, overflowY: 'auto', minHeight: '100vh'}}
    >
      <div className='position-relative w-100'>
        <img
          src={toAbsoluteUrl('/img/pegadaian-bg.png')}
          alt='BG'
          style={{width: '100%', height: '80px', objectFit: 'cover'}}
        />
        <button onClick={onClose} className='btn position-absolute top-0 start-0 mt-4 ms-2'>
          <i className='bi bi-x-lg fs-2 text-white'></i>
        </button>
      </div>

      <div className='px-4 mt-2'>
        <h4 className='fw-bolder mb-1'>{title}</h4>
        <p className='text-muted small mb-5'>
          Pilih dari daftar atau ketik untuk mempersempit pencarian.
        </p>

        {/* Input Filter */}
        <div className='input-group border rounded-3 p-3 mb-5 shadow-sm align-items-center bg-light'>
          <i className='bi bi-search text-success fs-3 me-3'></i>
          <input
            type='text'
            autoFocus
            className='form-control border-0 p-0 bg-transparent'
            placeholder={`Filter nama ${placeholder}...`}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{boxShadow: 'none'}}
          />
        </div>

        <div className='location-list'>
          <div className='px-2'>
            {loading ? (
              <div className='text-center py-5'>
                <div className="spinner-border text-success" role="status"></div>
                <div className='mt-2'>Memuat data...</div>
              </div>
            ) : filteredResults.length === 0 ? (
              <div className='text-center mt-10 text-muted'>
                Lokasi "{searchValue}" tidak ditemukan.
              </div>
            ) : (
              filteredResults.map((loc, index) => (
                <div
                  key={index}
                  className='d-flex align-items-start mb-5 cursor-pointer'
                  onClick={() => onSelect(loc, mode)}
                >
                  <div className='me-4'>
                    <i className='bi bi-bank2 text-success fs-1'></i>
                  </div>
                  <div className='border-bottom w-100 pb-4'>
                    <h5 className='fw-bolder mb-1 text-dark'>{loc}</h5>
                    <small className="text-muted">{point}</small>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LocationSearchOverlay
